import { Box, Flex, Title } from "@mantine/core";
import isEqual from "lodash/isEqual";
import { useEffect, useRef } from "react";
import type { Control, FieldError, FieldErrors } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { Person } from "../../../../utils/backend-types";
import { getErrorText } from "../../../../utils/get-error-text";
import { type applyHighlightingToObjectFormFieldData } from "../../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormFieldInfoText } from "../../../BuildingBlocks/Forms/FormFieldInfoText/FormFieldInfoText";
import { CustomFormFieldWrapper } from "../../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import {
  type PERSON_FORM_FIELD_DATA,
  TAX_FORMS_APPLICANT_NUMBER_INFO_TEXT,
  TAX_FORMS_CONTACT_NAME_INFO_TEXT,
  UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_CHANGED_TOAST_MESSAGE,
  UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_INFO_TEXT
} from "../PersonForm.constants";
import { ChangeableSupplierState } from "./ChangeableSupplierState";

interface EditFieldsProps {
  control: Control<Person>;
  errors: FieldErrors<Person>;
  formFieldData: ReturnType<
    typeof applyHighlightingToObjectFormFieldData<
      Person,
      typeof PERSON_FORM_FIELD_DATA
    >
  >["formFieldData"];
}

type TaxFormFieldsProps = EditFieldsProps & {
  persons: Array<Person>;
  personId: number;
  taxFormsSubmissionInOwnName: boolean | null;
};

function TaxFormFields({
  control,
  errors,
  formFieldData,
  persons,
  personId,
  taxFormsSubmissionInOwnName
}: TaxFormFieldsProps) {
  const { showToast } = useToast();
  const unternehmenInSchwierigkeitenYears = useWatch({
    control,
    name: "unternehmenInSchwierigkeitenYears"
  });
  const initialUnternehmenInSchwierigkeitenYearsRef = useRef(
    unternehmenInSchwierigkeitenYears
  );
  const hasShownSchwierigkeitenToastRef = useRef(false);

  useEffect(() => {
    const yearsChanged = !isEqual(
      unternehmenInSchwierigkeitenYears,
      initialUnternehmenInSchwierigkeitenYearsRef.current
    );

    if (yearsChanged && !hasShownSchwierigkeitenToastRef.current) {
      hasShownSchwierigkeitenToastRef.current = true;
      showToast({
        type: "info",
        message: UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_CHANGED_TOAST_MESSAGE
      });
    }
  }, [unternehmenInSchwierigkeitenYears, showToast]);

  const filteredPersons = persons.filter(
    (person) => person.shouldRegulatoryDutiesBeChecked && person.id !== personId
  );
  const personOptions = filteredPersons.map((person) => ({
    label: person.name,
    value: person.id.toString()
  }));
  const updatedTaxFormsCompanyRepresentedByData = {
    ...formFieldData.taxFormsCompanyRepresentedBy,
    data: personOptions
  };

  return (
    <>
      <Title mb={0} mt="xl" order={6}>
        Allgemein
      </Title>
      <FormFieldController
        control={control}
        data={formFieldData.customsOffice}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsSubmissionInOwnName}
      />
      {taxFormsSubmissionInOwnName === false && (
        <>
          <FormFieldController
            control={control}
            data={updatedTaxFormsCompanyRepresentedByData}
          />
          <FormFieldInfoText infoText='Bitte legen Sie das Unternehmen an, wenn es noch nicht im Projekt vorhanden ist und aktivieren Sie das Add-on "Stromsteuer".' />
          <FormFieldController
            control={control}
            data={formFieldData.taxFormsRepresentationType}
          />
        </>
      )}

      <Title mb={0} mt="xl" order={6}>
        Antragsteller Steuerformulare
      </Title>
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantName}
      />
      <Controller
        control={control}
        name={formFieldData.versorgerstatusPeriods.name}
        render={({ field: { onChange, value } }) => (
          <CustomFormFieldWrapper
            error={
              errors.versorgerstatusPeriods
                ? Object.values(errors.versorgerstatusPeriods).reduce<string>(
                    (acc, period) => {
                      acc += getErrorText(period as FieldError);
                      return acc;
                    },
                    ""
                  )
                : undefined
            }
            helpText={formFieldData.versorgerstatusPeriods.helpText}
            highlight={formFieldData.versorgerstatusPeriods.highlight}
            label={formFieldData.versorgerstatusPeriods.label}
            name={formFieldData.versorgerstatusPeriods.name}
          >
            <ChangeableSupplierState
              versorgerstatusPeriods={value}
              onChange={onChange}
            />
          </CustomFormFieldWrapper>
        )}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantDateOfBirthOrFoundation}
      />
      <Flex gap="md">
        <Box style={{ flex: 3 }}>
          <FormFieldController
            control={control}
            data={formFieldData.taxFormsApplicantStreet}
          />
        </Box>
        <Box style={{ flex: 1 }}>
          <FormFieldController
            control={control}
            data={formFieldData.taxFormsApplicantHouseNumber}
          />
        </Box>
      </Flex>
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantAddressSupplement}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantZip}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantCity}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantCityDistrict}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantCountry}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantResidenceOrOffice}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantOtherCity}
      />

      <Title mb={0} mt="xl" order={6}>
        EU-Beihilferecht
      </Title>
      <FormFieldInfoText
        infoText={UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_INFO_TEXT}
      />
      <FormFieldController
        control={control}
        data={formFieldData.unternehmenInSchwierigkeitenYears}
        error={errors.unternehmenInSchwierigkeitenYears?.[0]}
      />

      <Title mb={0} mt="xl" order={6}>
        Zusatzinformationen für die HZA-Kommunikation
      </Title>

      <FormFieldInfoText infoText={TAX_FORMS_APPLICANT_NUMBER_INFO_TEXT} />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantNumber}
      />
      <FormFieldController
        control={control}
        data={formFieldData.companyNumber}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsApplicantTransactionNumberElectricityTax}
      />

      <Title mb={0} mt="xl" order={6}>
        Ansprechpartner Steuerformulare
      </Title>

      <FormFieldInfoText infoText={TAX_FORMS_CONTACT_NAME_INFO_TEXT} />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsContactName}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsContactPhone}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsContactFax}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsContactEmail}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsContactWebsite}
      />

      <Title mb={0} mt="xl" order={6}>
        Bankverbindung Steuerformulare
      </Title>
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsBankAccountHolder}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsBankAccountIban}
      />
      <FormFieldController
        control={control}
        data={formFieldData.taxFormsBankAccountBic}
      />
    </>
  );
}

export { TaxFormFields, type TaxFormFieldsProps };
